import api from './api';
import { AUTH_URLS } from '../components/config/apiConfig';
import axios from 'axios';
// Import the API_BASE_URL from apiConfig.js
import apiConfig from '../components/config/apiConfig';

// Replace hardcoded API_URL with the imported value
const API_URL = apiConfig.BASE_URL;

const saveAdminSession = (data) => {
  localStorage.setItem('adminToken', data.token);
  localStorage.setItem('adminId', data.admin.id);
  localStorage.setItem('adminName', data.admin.username);
  localStorage.setItem('adminEmail', data.admin.email);
  localStorage.setItem('adminIsSuperAdmin', data.admin.is_super_admin);
  localStorage.setItem('adminIsViewOnly', data.admin.is_view_only);
  localStorage.setItem('adminTokenExpires', data.expires);
};

const saveMemberSession = (data, rememberMe = true) => {
  // Store the token expiration date
  const expiresAt = data.expires || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  
  if (rememberMe) {
    localStorage.setItem('memberToken', data.token);
    localStorage.setItem('memberId', data.member.id);
    localStorage.setItem('memberName', data.member.name);
    localStorage.setItem('memberEmail', data.member.email);
    localStorage.setItem('memberType', data.member.membership_type);
    localStorage.setItem('memberTokenExpires', expiresAt);
    
    // Clear session storage if it exists
    sessionStorage.removeItem('memberToken');
    sessionStorage.removeItem('memberId');
    sessionStorage.removeItem('memberName');
    sessionStorage.removeItem('memberEmail');
    sessionStorage.removeItem('memberType');
    sessionStorage.removeItem('memberTokenExpires');
  } else {
    // Use session storage instead of localStorage for "don't remember me"
    sessionStorage.setItem('memberToken', data.token);
    sessionStorage.setItem('memberId', data.member.id);
    sessionStorage.setItem('memberName', data.member.name);
    sessionStorage.setItem('memberEmail', data.member.email);
    sessionStorage.setItem('memberType', data.member.membership_type);
    sessionStorage.setItem('memberTokenExpires', expiresAt);
    
    // Clear localStorage if it exists
    localStorage.removeItem('memberToken');
    localStorage.removeItem('memberId');
    localStorage.removeItem('memberName');
    localStorage.removeItem('memberEmail');
    localStorage.removeItem('memberType');
    localStorage.removeItem('memberTokenExpires');
  }
};

const clearAdminSession = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminId');
  localStorage.removeItem('adminName');
  localStorage.removeItem('adminEmail');
  localStorage.removeItem('adminIsSuperAdmin');
  localStorage.removeItem('adminIsViewOnly');
  localStorage.removeItem('adminTokenExpires');
};

const clearMemberSession = () => {
  localStorage.removeItem('memberToken');
  localStorage.removeItem('memberId');
  localStorage.removeItem('memberName');
  localStorage.removeItem('memberEmail');
  localStorage.removeItem('memberType');
  localStorage.removeItem('memberTokenExpires');
  
  sessionStorage.removeItem('memberToken');
  sessionStorage.removeItem('memberId');
  sessionStorage.removeItem('memberName');
  sessionStorage.removeItem('memberEmail');
  sessionStorage.removeItem('memberType');
  sessionStorage.removeItem('memberTokenExpires');
};

const authService = {
  // Admin authentication
  adminLogin: async (credentials) => {
    const response = await api.post(AUTH_URLS.ADMIN_LOGIN, credentials);
    saveAdminSession(response.data);
    return response.data;
  },
  
  adminLogout: () => {
    clearAdminSession();
    return true;
  },
  
  isAdminLoggedIn: () => {
    const token = localStorage.getItem('adminToken');
    const tokenExpires = localStorage.getItem('adminTokenExpires');
    const isValid = token && tokenExpires && new Date(tokenExpires) > new Date();
    
    // If token is expired, clear the session
    if (token && !isValid) {
      clearAdminSession();
    }
    
    return isValid;
  },
  
  // Member authentication
  memberLogin: async (credentials, rememberMe = true) => {
    // Update the endpoint to use the imported API_URL
    const response = await axios.post(`${API_URL}/member/login`, credentials);
    
    // Store token and user data based on rememberMe preference
    if (response.data && response.data.token) {
      saveMemberSession(response.data, rememberMe);
    }
    
    return response.data;
  },
  
  memberLogout: () => {
    clearMemberSession();
    return true;
  },
  
  isMemberLoggedIn: () => {
    const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
    const tokenExpires = localStorage.getItem('memberTokenExpires') || sessionStorage.getItem('memberTokenExpires');
    
    if (!token || !tokenExpires) {
      return false;
    }
    
    const isValid = new Date(tokenExpires) > new Date();
    
    // If token is expired, clear the session
    if (!isValid) {
      clearMemberSession();
    }
    
    return isValid;
  },
  
  memberRegister: async (memberData) => {
    const response = await api.post(AUTH_URLS.MEMBER_REGISTER, memberData);
    return response.data;
  },
  
  // Password reset functions
  requestPasswordReset: async (email) => {
    const response = await api.post(AUTH_URLS.PASSWORD_RESET_REQUEST, { email });
    return response.data;
  },
  
  resetPassword: async (token, newPassword) => {
    const response = await api.post(AUTH_URLS.PASSWORD_RESET, { 
      token, 
      newPassword 
    });
    return response.data;
  },
  
  // Get stored tokens
  getAdminToken: () => {
    return localStorage.getItem('adminToken');
  },
  
  getMemberToken: () => {
    return localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
  },
  
  /**
   * Logout the current user
   */
  logout: () => {
    // Clear both storage options
    clearMemberSession();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  },
  
  /**
   * Get the current authentication token
   * @returns {string|null} - The authentication token or null
   */
  getToken: () => {
    // Check member tokens first (from both storage options)
    const memberToken = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
    if (memberToken) return memberToken;
    
    // Fall back to general token
    return localStorage.getItem('token') || sessionStorage.getItem('token') || null;
  },
  
  /**
   * Check if user is authenticated
   * @returns {boolean} - True if authenticated, false otherwise
   */
  isAuthenticated: () => {
    // Check if member is logged in first
    if (authService.isMemberLoggedIn()) {
      return true;
    }
    
    // Fall back to general token check
    const token = authService.getToken();
    return !!token;
  }
};

export default authService;
